<template>
  <div class="flex font-medium leading-6">{{ paymentCollapsed }}</div>
  <div v-if="!!cardDetails && !paymentCollapsed" class="flex flex-col font-medium">
    <span class="font-bold leading-6">{{ paymentFirstLine }}</span>
    <ul class="flex max-w-[250px] flex-col font-medium leading-6">
      <li>
        <span>{{ paymentExpirationLine }}</span>
      </li>
      <li>
        <span class="capitalize">{{ cardDetails.cardholderName?.toLocaleLowerCase() }}</span>
      </li>
      <li v-if="cardDetails.isDefault" class="mt-2.5 flex italic leading-6 text-[--color-neutral-a2]">
        {{ $t("pages.account.payments.payment_default") }}
      </li>
    </ul>
  </div>
  <hr class="my-5" />
  <div class="flex flex-col font-medium">
    <span class="font-bold leading-6">{{ $t("shared.checkout.billing_details_section.labels.billing_address") }}</span>
    <span v-if="billingAddressEqualsShipping" class="text-base font-medium leading-6 text-[--color-neutral-a2]">
      {{ $t("shared.checkout.billing_details_section.labels.same_as_shipping_address") }}</span
    >
  </div>
  <ul v-if="billingAddress && !billingAddressEqualsShipping" class="flex max-w-[250px] flex-col font-medium leading-6">
    <!-- Japan-specific address format -->
    <template v-if="billingAddress?.countryCode === 'JPN'">
      <li class="mb-2 font-semibold">{{ billingAddress?.lastName }} {{ billingAddress?.firstName }}</li>
      <li>{{ billingAddress?.postalCode }}</li>
      <li>{{ billingAddress?.regionName }}</li>
      <li>{{ billingAddress?.city }}</li>
      <li>{{ billingAddress?.line1 }}</li>
      <li v-if="billingAddress?.buildingTL">{{ billingAddress?.buildingTL }}</li>
      <li>{{ billingAddress?.countryName }}</li>
      <li class="mt-2">{{ billingAddress?.phone }}</li>
      <li class="break-all">{{ billingAddress?.email }}</li>
    </template>
    <template v-else>
      <li v-if="billingAddress?.countryCode === 'CHN'" class="mb-2 font-semibold">
        {{ billingAddress?.lastName }} {{ billingAddress?.firstName }}
      </li>
      <li v-else class="mb-2 font-semibold">{{ billingAddress?.firstName }} {{ billingAddress?.lastName }}</li>
      <li v-if="billingAddress?.organization">{{ billingAddress?.organization }}</li>
      <li v-if="billingAddress?.departmentTL">{{ billingAddress?.departmentTL }}</li>
      <li v-if="billingAddress?.buildingTL || billingAddress?.mailStopTL">
        {{ billingAddress?.buildingTL ?? "" }} {{ billingAddress?.mailStopTL ?? "" }}
      </li>
      <li>{{ billingAddress?.line1 }}</li>
      <li v-if="isMUCCountry(billingAddress?.countryCode!)">
        {{ billingAddress?.regionName || billingAddress?.countyTL }} {{ billingAddress?.postalCode }}
        {{ billingAddress?.city }}
      </li>
      <li v-else>
        {{ billingAddress?.city }}, {{ billingAddress?.regionName || billingAddress?.countyTL }}
        {{ billingAddress?.postalCode }}
      </li>
      <li class="mb-3">{{ billingAddress?.countryName }}</li>
      <li>{{ billingAddress?.phone }}</li>
      <li>{{ billingAddress?.email }}</li>
    </template>
  </ul>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { isMUCCountry } from "@/core/utilities/address";
import { getInvoiceMeNotificationText } from "@/core/utilities/orders";
import { useCheckout } from "@/shared/checkout/composables";
const { billingAddressEqualsShipping, paymentMethodDetailsForReview } = useCheckout();
const { t } = useI18n();
import { useUser } from "@/shared/account";
import { getCardTypeName } from "@/core/utilities/payments";
const { user } = useUser();
import { useCheckoutFacade } from "../../composables/useCheckoutFacade";

const { checkoutPaymentMethod, billingAddress } = useCheckoutFacade();

const cardDetails = computed(() => {
  return paymentMethodDetailsForReview.value;
});
const paymentCollapsed = computed(() => {
  const netTerms = user.value?.contact?.organizations?.items?.[0]?.netTerms ?? "";
  if (checkoutPaymentMethod.value === "NetTerms") {
    return getInvoiceMeNotificationText(netTerms, t);
  } else if (checkoutPaymentMethod.value === "Quote") {
    return t("pages.checkout.quote_payment");
  } else {
    return "";
  }
});

const paymentFirstLine = computed(() => {
  const cardTypeName = getCardTypeName(cardDetails.value?.cardType ?? "");
  const formattedCardTypeName = cardTypeName.charAt(0).toUpperCase() + cardTypeName.slice(1).toLowerCase();
  return `${formattedCardTypeName} Ending in ${cardDetails.value?.maskedCardNumber?.slice(-4)}`;
});
const paymentExpirationLine = computed(() => {
  return `${t("common.labels.expiration")}: ${cardDetails.value?.expirationMonth}/${cardDetails.value?.expirationYear?.slice(-2)}`;
});
</script>
